import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSwot } from '../context/SwotContext';

const { FiX, FiFileText, FiDownload } = FiIcons;

const ExportModal = ({ onClose }) => {
  const swotData = useSwot();

  const exportToCSV = () => {
    const csvContent = [
      ['Category', 'Item', 'Priority', 'Created At'],
      ...swotData.strengths.map(item => ['Strengths', item.text, item.priority, item.createdAt]),
      ...swotData.weaknesses.map(item => ['Weaknesses', item.text, item.priority, item.createdAt]),
      ...swotData.opportunities.map(item => ['Opportunities', item.text, item.priority, item.createdAt]),
      ...swotData.threats.map(item => ['Threats', item.text, item.priority, item.createdAt])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `swot-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToWord = async () => {
    // Dynamic import to ensure proper bundling
    try {
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, AlignmentType, BorderStyle, WidthType, TableLayoutType } = await import('docx');
      const { saveAs } = await import('file-saver');
      
      // Helper function to create item list
      const createItemsList = (items, color) => {
        return items.map(item => {
          return new Paragraph({
            children: [
              new TextRun({
                text: "• ",
                bold: true,
                color: color,
              }),
              new TextRun({
                text: item.text,
                bold: false,
              }),
              new TextRun({
                text: ` (Priority: ${item.priority})`,
                italics: true,
                color: "808080",
              }),
            ],
            spacing: {
              after: 120,
            },
          });
        });
      };

      // Create quadrant table for SWOT matrix
      const createSwotMatrix = () => {
        return new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  width: {
                    size: 50,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [
                    new Paragraph({
                      text: "STRENGTHS",
                      heading: HeadingLevel.HEADING_3,
                      alignment: AlignmentType.CENTER,
                      color: "2E7D32", // Green
                      spacing: {
                        before: 200,
                        after: 200,
                      },
                    }),
                    ...createItemsList(swotData.strengths.slice(0, 5), "2E7D32"), // Show top 5
                    swotData.strengths.length > 5 ? new Paragraph({
                      text: `... and ${swotData.strengths.length - 5} more`,
                      italics: true,
                      alignment: AlignmentType.CENTER,
                    }) : new Paragraph({}),
                  ],
                  shading: {
                    fill: "F1F8E9", // Light green background
                  },
                }),
                new TableCell({
                  width: {
                    size: 50,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [
                    new Paragraph({
                      text: "WEAKNESSES",
                      heading: HeadingLevel.HEADING_3,
                      alignment: AlignmentType.CENTER,
                      color: "C62828", // Red
                      spacing: {
                        before: 200,
                        after: 200,
                      },
                    }),
                    ...createItemsList(swotData.weaknesses.slice(0, 5), "C62828"), // Show top 5
                    swotData.weaknesses.length > 5 ? new Paragraph({
                      text: `... and ${swotData.weaknesses.length - 5} more`,
                      italics: true,
                      alignment: AlignmentType.CENTER,
                    }) : new Paragraph({}),
                  ],
                  shading: {
                    fill: "FFEBEE", // Light red background
                  },
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  width: {
                    size: 50,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [
                    new Paragraph({
                      text: "OPPORTUNITIES",
                      heading: HeadingLevel.HEADING_3,
                      alignment: AlignmentType.CENTER,
                      color: "1565C0", // Blue
                      spacing: {
                        before: 200,
                        after: 200,
                      },
                    }),
                    ...createItemsList(swotData.opportunities.slice(0, 5), "1565C0"), // Show top 5
                    swotData.opportunities.length > 5 ? new Paragraph({
                      text: `... and ${swotData.opportunities.length - 5} more`,
                      italics: true,
                      alignment: AlignmentType.CENTER,
                    }) : new Paragraph({}),
                  ],
                  shading: {
                    fill: "E3F2FD", // Light blue background
                  },
                }),
                new TableCell({
                  width: {
                    size: 50,
                    type: WidthType.PERCENTAGE,
                  },
                  children: [
                    new Paragraph({
                      text: "THREATS",
                      heading: HeadingLevel.HEADING_3,
                      alignment: AlignmentType.CENTER,
                      color: "E65100", // Orange
                      spacing: {
                        before: 200,
                        after: 200,
                      },
                    }),
                    ...createItemsList(swotData.threats.slice(0, 5), "E65100"), // Show top 5
                    swotData.threats.length > 5 ? new Paragraph({
                      text: `... and ${swotData.threats.length - 5} more`,
                      italics: true,
                      alignment: AlignmentType.CENTER,
                    }) : new Paragraph({}),
                  ],
                  shading: {
                    fill: "FFF3E0", // Light orange background
                  },
                }),
              ],
            }),
          ],
          tableLayout: TableLayoutType.FIXED,
        });
      };

      // Create summary table
      const createSummaryTable = () => {
        return new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: "Category", bold: true })],
                  shading: { fill: "F5F5F5" },
                }),
                new TableCell({
                  children: [new Paragraph({ text: "Count", bold: true })],
                  shading: { fill: "F5F5F5" },
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph("Strengths")],
                }),
                new TableCell({
                  children: [new Paragraph(swotData.strengths.length.toString())],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph("Weaknesses")],
                }),
                new TableCell({
                  children: [new Paragraph(swotData.weaknesses.length.toString())],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph("Opportunities")],
                }),
                new TableCell({
                  children: [new Paragraph(swotData.opportunities.length.toString())],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph("Threats")],
                }),
                new TableCell({
                  children: [new Paragraph(swotData.threats.length.toString())],
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: "Total", bold: true })],
                  shading: { fill: "F5F5F5" },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      text: (
                        swotData.strengths.length +
                        swotData.weaknesses.length +
                        swotData.opportunities.length +
                        swotData.threats.length
                      ).toString(),
                      bold: true,
                    }),
                  ],
                  shading: { fill: "F5F5F5" },
                }),
              ],
            }),
          ],
        });
      };

      // Create detailed lists for each category
      const createDetailedSection = (title, items, color) => {
        return [
          new Paragraph({
            text: title,
            heading: HeadingLevel.HEADING_2,
            color: color,
            spacing: {
              before: 400,
              after: 200,
            },
          }),
          ...createItemsList(items, color),
        ];
      };

      // Create Word document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // Title
              new Paragraph({
                text: swotData.title || "SWOT Analysis",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: {
                  before: 240,
                  after: 240,
                },
              }),
              
              // Description (if available)
              ...(swotData.description
                ? [
                    new Paragraph({
                      text: swotData.description,
                      alignment: AlignmentType.CENTER,
                      spacing: {
                        before: 200,
                        after: 400,
                      },
                    }),
                  ]
                : []),
              
              // Generated date
              new Paragraph({
                text: `Generated on: ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
                spacing: {
                  before: 200,
                  after: 400,
                },
                italics: true,
              }),
              
              // SWOT Matrix
              new Paragraph({
                text: "SWOT Analysis Matrix",
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  before: 400,
                  after: 200,
                },
              }),
              
              createSwotMatrix(),
              
              // Summary Statistics
              new Paragraph({
                text: "Analysis Summary",
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  before: 400,
                  after: 200,
                },
              }),
              
              createSummaryTable(),
              
              // Detailed Sections
              ...createDetailedSection("Detailed Strengths", swotData.strengths, "2E7D32"),
              ...createDetailedSection("Detailed Weaknesses", swotData.weaknesses, "C62828"),
              ...createDetailedSection("Detailed Opportunities", swotData.opportunities, "1565C0"),
              ...createDetailedSection("Detailed Threats", swotData.threats, "E65100"),
            ],
          },
        ],
      });

      // Generate and save the Word document
      const buffer = await Packer.toBlob(doc);
      saveAs(buffer, `swot-analysis-${new Date().toISOString().split('T')[0]}.docx`);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Error generating Word document. Please try again.");
    }
  };

  const exportToText = () => {
    const formatItems = (items) => {
      return items.map(item => 
        `• ${item.text} (Priority: ${item.priority})`
      ).join('\n');
    };

    const content = `
${swotData.title || "SWOT Analysis"}

${swotData.description ? swotData.description + '\n' : ''}
Generated on: ${new Date().toLocaleDateString()}

STRENGTHS
${formatItems(swotData.strengths)}

WEAKNESSES
${formatItems(swotData.weaknesses)}

OPPORTUNITIES
${formatItems(swotData.opportunities)}

THREATS
${formatItems(swotData.threats)}

Analysis Summary:
- Total Strengths: ${swotData.strengths.length}
- Total Weaknesses: ${swotData.weaknesses.length}
- Total Opportunities: ${swotData.opportunities.length}
- Total Threats: ${swotData.threats.length}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `swot-analysis-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Export Report</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => {
              exportToCSV();
              onClose();
            }}
            className="w-full flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition-colors group"
          >
            <div className="p-3 bg-green-600 text-white rounded-lg group-hover:bg-green-700 transition-colors">
              <SafeIcon icon={FiFileText} className="w-6 h-6" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-800">Export as CSV</h3>
              <p className="text-sm text-gray-600">Spreadsheet-compatible format</p>
            </div>
            <SafeIcon icon={FiDownload} className="w-5 h-5 text-green-600 ml-auto" />
          </button>

          <button
            onClick={() => {
              exportToWord();
              onClose();
            }}
            className="w-full flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors group"
          >
            <div className="p-3 bg-blue-600 text-white rounded-lg group-hover:bg-blue-700 transition-colors">
              <SafeIcon icon={FiFileText} className="w-6 h-6" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-800">Export as Word Document</h3>
              <p className="text-sm text-gray-600">Beautiful formatted Word report</p>
            </div>
            <SafeIcon icon={FiDownload} className="w-5 h-5 text-blue-600 ml-auto" />
          </button>
          
          <button
            onClick={() => {
              exportToText();
              onClose();
            }}
            className="w-full flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors group"
          >
            <div className="p-3 bg-gray-600 text-white rounded-lg group-hover:bg-gray-700 transition-colors">
              <SafeIcon icon={FiFileText} className="w-6 h-6" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-800">Export as Text Report</h3>
              <p className="text-sm text-gray-600">Simple text format</p>
            </div>
            <SafeIcon icon={FiDownload} className="w-5 h-5 text-gray-600 ml-auto" />
          </button>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <h4 className="font-semibold text-gray-800 mb-2">Export Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Strengths:</span>
              <span className="font-medium ml-2">{swotData.strengths.length}</span>
            </div>
            <div>
              <span className="text-gray-600">Weaknesses:</span>
              <span className="font-medium ml-2">{swotData.weaknesses.length}</span>
            </div>
            <div>
              <span className="text-gray-600">Opportunities:</span>
              <span className="font-medium ml-2">{swotData.opportunities.length}</span>
            </div>
            <div>
              <span className="text-gray-600">Threats:</span>
              <span className="font-medium ml-2">{swotData.threats.length}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExportModal;